import { useEffect, useState } from 'react'
import { Dialog } from '../ui/Dialog.js'
import { Button } from '../ui/Button.js'
import { Textarea } from '../ui/Textarea.js'
import { Field } from '../ui/Field.js'
import { Text } from '../ui/Typography.js'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  /**
   * What will actually happen, in plain words.
   *
   * *"Pausing asks first"* — and the asking is only worth anything if it says the
   * consequence. "Are you sure?" is a question nobody can answer.
   */
  consequence: React.ReactNode
  confirmLabel: string
  cancelLabel?: string
  /** The error ink, for something that takes access away or cannot be undone. */
  destructive?: boolean
  loading?: boolean
  error?: string | null
  /**
   * Ask why, and pass it back.
   *
   * Ten writes on the desk take a reason and four of them refuse without one —
   * cancelling a booking, cancelling a class, refunding a payment, and opening
   * somebody's health record. Every one of those is a moment where the person
   * affected may later ask what happened, and the reason is the entire answer.
   *
   * It belongs here rather than in a dialog of its own: it is the same question,
   * asked at the same moment, with one more field. Two dialogs would drift apart
   * and the confirmation for a refund would stop looking like the one for a
   * cancellation.
   *
   * Leave it off and the dialog is exactly what it was.
   */
  reason?: {
    /** Blocks the confirm button until something is typed. */
    required?: boolean
    label?: string
    placeholder?: string
    maxLength?: number
  }
  /** Receives the reason when one was asked for, and nothing when it was not. */
  onConfirm: (reason?: string) => void
}

/** The question asked before something happens that somebody would want warned about. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  consequence,
  confirmLabel,
  cancelLabel = 'Keep it as it is',
  destructive,
  loading,
  error,
  reason,
  onConfirm,
}: ConfirmDialogProps) {
  const [why, setWhy] = useState('')

  /**
   * Emptied every time it opens.
   *
   * Without this, refusing a refund and then refunding a different one arrives with
   * the first refund's reason already typed in — and the reason is written into the
   * audit log, where it becomes a sentence about the wrong payment.
   */
  useEffect(() => {
    if (open) setWhy('')
  }, [open])

  const missing = Boolean(reason?.required) && why.trim().length === 0

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={title}
      description={consequence}
      footer={
        <>
          <Button variant="tertiary" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            onClick={() => onConfirm(reason ? why.trim() : undefined)}
            loading={loading}
            // Disabled rather than failing on submit: the server refuses without one,
            // and finding that out after pressing the button on a refund is a bad way
            // to learn it.
            disabled={missing}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {reason && (
        <Field
          label={reason.label ?? 'Why?'}
          // Named as what it is for, not as paperwork: it is read back later by
          // somebody asking what happened.
          hint={
            reason.required
              ? 'Recorded against this, and shown to whoever asks about it later.'
              : 'Optional. Recorded against this if you write one.'
          }
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              rows={3}
              value={why}
              maxLength={reason.maxLength ?? 500}
              /* There is always one. Four writes on this desk refuse without
                 a reason, and an empty box beside a question like "Suspend
                 Dana Kwan?" gives no idea what sort of answer is wanted —
                 every caller would have had to remember to pass one, and
                 none of them did. */
              placeholder={reason.placeholder ?? 'Say why, in a sentence'}
              onChange={(e) => setWhy(e.target.value)}
              disabled={loading}
            />
          )}
        </Field>
      )}
      {error && (
        <Text variant="secondary" className="text-error" aria-live="polite">
          {error}
        </Text>
      )}
    </Dialog>
  )
}
